/**
 * Shared table pieces for every 035 report (task 035-01, README decision 10):
 * the empty-not-zero cell rule, the totals-row title string, a generic
 * `SortableHeader` (copied from `campaigns.tsx:179–215` — that file is a copy
 * source, never exported from), client-side sorting, and the three loading /
 * empty / error states every screen renders identically. No data fetching
 * here — each screen owns its `*-api.ts` module.
 */

import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { fmtCount } from '../../lib/format';
import { TableCell, TableRow } from '../../components/ui/table';

/** A zero renders as an empty cell (Q63 §7.3); `fmtCount(0)` itself returns '0'. */
export function cell(n: number): string {
  return n === 0 ? '' : fmtCount(n);
}

export const UNIQUE_TOTAL_TITLE = 'Distinct across the selection — not the sum of the rows';

export type SortDir = 'asc' | 'desc';

export interface Sort<K extends string> {
  key: K;
  dir: SortDir;
}

interface SortableHeaderProps<K extends string> {
  label: string;
  sortKey: K;
  sort: Sort<K> | null;
  onToggle: (key: K) => void;
  /**
   * Optional column-help tooltip, rendered as the `<th>`'s `title` (task
   * 035-03 addition — the shared header had no way to carry column help;
   * additive, every existing call site is unaffected).
   */
  title?: string;
}

/**
 * Sortable column header: clicking cycles none → asc → desc → none. The
 * active column carries `aria-sort` and a single direction glyph; inactive
 * sortable headers show BOTH arrows — the "sort is off" affordance. The
 * glyphs are aria-hidden so the accessible name stays the bare column label.
 */
export function SortableHeader<K extends string>({
  label,
  sortKey,
  sort,
  onToggle,
  title,
}: SortableHeaderProps<K>) {
  const active = sort !== null && sort.key === sortKey;
  const dir: SortDir | null = active && sort ? sort.dir : null;

  return (
    <th
      className="px-4 py-2.5 font-semibold whitespace-nowrap"
      aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : undefined}
      title={title}
    >
      <button
        type="button"
        onClick={() => onToggle(sortKey)}
        className="inline-flex cursor-pointer items-center gap-1 transition-colors hover:text-ink"
      >
        {label}
        <div
          aria-hidden="true"
          className={dir ? '' : 'text-faint inline-flex flex-col'}
          title={dir ? undefined : 'Sort is off — click to sort A-Z'}
        >
          <span>{dir === 'desc' ? '▼' : '▲'}</span>
          <span className="-mt-1">{!dir ? '▼' : ''}</span>
        </div>
      </button>
    </th>
  );
}

/**
 * Client-side sort over `rows` (hundreds at most — README decision 10): none →
 * asc → desc → none per `toggleSort(key)`; strings compare via `localeCompare`,
 * numbers numerically, `null` sorts last in either direction. A `null` cmp tie
 * and an equal-value tie both fall back to the original row order (stable).
 */
export function useClientSort<T, K extends string>(
  rows: T[],
  accessor: (row: T, key: K) => string | number | null
): { sortedRows: T[]; sort: Sort<K> | null; toggleSort: (key: K) => void } {
  const [sort, setSort] = useState<Sort<K> | null>(null);

  const toggleSort = (key: K) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  };

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const { key, dir } = sort;
    const indexed = rows.map((row, index) => ({ row, index }));
    indexed.sort((a, b) => {
      const va = accessor(a.row, key);
      const vb = accessor(b.row, key);
      if (va === null && vb === null) return a.index - b.index;
      if (va === null) return 1;
      if (vb === null) return -1;
      const cmp =
        typeof va === 'string' && typeof vb === 'string'
          ? va.localeCompare(vb)
          : Number(va) - Number(vb);
      if (cmp === 0) return a.index - b.index;
      return dir === 'asc' ? cmp : -cmp;
    });
    return indexed.map((w) => w.row);
  }, [rows, sort, accessor]);

  return { sortedRows, sort, toggleSort };
}

interface TotalsRowProps {
  testId: string;
  cells: ReactNode;
}

/** Wraps the caller's fully-rendered `<TableCell>`s (formatting/titles stay per-screen). */
export function TotalsRow({ testId, cells }: TotalsRowProps) {
  return <TableRow data-testid={testId}>{cells}</TableRow>;
}

interface LoadingRowProps {
  colSpan: number;
  message?: string;
}

/** 007's loading string, `co-op-counts.tsx:449–455`. */
export function LoadingRow({ colSpan, message = 'Loading…' }: LoadingRowProps) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} className="py-6 text-center text-muted-foreground">
        {message}
      </TableCell>
    </TableRow>
  );
}

interface EmptyRowProps {
  colSpan: number;
  message: string;
}

/** `co-op-counts.tsx:458–464`'s markup, per-screen message. */
export function EmptyRow({ colSpan, message }: EmptyRowProps) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} className="py-6 text-center text-muted-foreground">
        {message}
      </TableCell>
    </TableRow>
  );
}

interface ErrorBannerProps {
  message: string;
}

/** `co-op-counts.tsx:430–434`'s markup. */
export function ErrorBanner({ message }: ErrorBannerProps) {
  return (
    <p className="border-b border-line bg-destructive/10 px-4 py-3 text-sm text-destructive">
      {message}
    </p>
  );
}
